import React, { useEffect, useState } from 'react';
import { Button, Table, Tag, Modal, Form, Input, Divider, Space, Popconfirm } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import restaurantService from '../services/restaurantService';
import { notify } from '../components/Customs/NotifyProvider';

const ManageRestaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [saving, setSaving] = useState(false);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      const res = await restaurantService.listRestaurants();
      setRestaurants(res.data || []);
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRestaurants();
  }, []);

  const handleCreate = async (values) => {
    try {
      setSaving(true);
      const res = await restaurantService.createRestaurant(values);

      if (!res?.status) {
        notify.error(res?.message || 'Failed to create restaurant');
        return;
      }

      notify.success(`"${values.name}" created — its admin can now log in as "${values.username}".`);
      setOpenAddModal(false);
      addForm.resetFields();
      loadRestaurants();
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || 'Failed to create restaurant');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (record) => {
    setEditingRestaurant(record);
    editForm.setFieldsValue({
      name: record.name,
      admin_full_name: record.admin_name,
      admin_username: record.admin_username,
      admin_email: record.admin_email,
      admin_phone: record.admin_phone,
    });
    setOpenEditModal(true);
  };

  const handleEdit = async (values) => {
    try {
      setSaving(true);
      const res = await restaurantService.updateRestaurant(editingRestaurant.id, {
        ...values,
        admin_user_id: editingRestaurant.admin_user_id,
      });

      if (!res?.status) {
        notify.error(res?.message || 'Failed to update restaurant');
        return;
      }

      notify.success('Restaurant updated successfully');
      setOpenEditModal(false);
      setEditingRestaurant(null);
      loadRestaurants();
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || 'Failed to update restaurant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await restaurantService.deleteRestaurant(record.id);

      if (!res?.status) {
        notify.error(res?.message || 'Failed to delete restaurant');
        return;
      }

      notify.success(`"${record.name}" and all its data deleted`);
      loadRestaurants();
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || 'Failed to delete restaurant');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'Restaurant Name', dataIndex: 'name' },
    {
      title: 'Admin Name',
      dataIndex: 'admin_name',
      render: (v) => v || '-',
    },
    {
      title: 'Admin Username',
      dataIndex: 'admin_username',
      render: (v) => (v ? <span className="username-text">{v}</span> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 110,
      render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      render: (v) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            shape="circle"
            icon={<EditOutlined />}
            className="table-icon-btn edit-btn"
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete restaurant"
            description={`Permanently delete "${record.name}" and ALL its data — users, tables, bills, menu items? This cannot be undone.`}
            okText="Yes, delete"
            okButtonProps={{ danger: true }}
            cancelText="No"
            onConfirm={() => handleDelete(record)}
          >
            <Button
              size="small"
              shape="circle"
              icon={<DeleteOutlined />}
              className="table-icon-btn delete-btn"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <ShopOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Manage Restaurants</h2>
            <p className="user-page-subtitle">
              Create a new restaurant and its first admin login in one step.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            icon={<ReloadOutlined />}
            className="secondary-btn"
            onClick={loadRestaurants}
            loading={loading}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="primary-btn"
            onClick={() => setOpenAddModal(true)}
          >
            Add Restaurant
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={restaurants}
          loading={loading}
        />
      </div>

      <Modal
        title="Add Restaurant"
        open={openAddModal}
        onCancel={() => { setOpenAddModal(false); addForm.resetFields(); }}
        onOk={() => addForm.submit()}
        confirmLoading={saving}
        okText="Create"
      >
        <Form form={addForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            label="Restaurant Name"
            name="name"
            rules={[{ required: true, message: 'Please enter the restaurant name' }]}
          >
            <Input placeholder="e.g. Spice Garden" />
          </Form.Item>

          <Divider style={{ margin: '8px 0 16px' }}>Admin Login</Divider>

          <Form.Item label="Role">
            <Input value="Admin" disabled />
          </Form.Item>

          <Form.Item
            label="Full Name"
            name="full_name"
            rules={[{ required: true, message: 'Please enter a full name' }]}
          >
            <Input placeholder="e.g. Spice Garden Admin" />
          </Form.Item>

          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Please enter a username' }]}
          >
            <Input placeholder="e.g. spicegardenadmin" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="e.g. admin@spicegarden.com" />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={[{ required: true, message: 'Please enter a phone number' }]}
          >
            <Input placeholder="e.g. 9876543210" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter a password' }]}
          >
            <Input.Password placeholder="Temporary password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Restaurant"
        open={openEditModal}
        onCancel={() => { setOpenEditModal(false); setEditingRestaurant(null); }}
        onOk={() => editForm.submit()}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            label="Restaurant Name"
            name="name"
            rules={[{ required: true, message: 'Please enter the restaurant name' }]}
          >
            <Input />
          </Form.Item>

          <Divider style={{ margin: '8px 0 16px' }}>Admin Login</Divider>

          <Form.Item label="Role">
            <Input value="Admin" disabled />
          </Form.Item>

          <Form.Item
            label="Admin Full Name"
            name="admin_full_name"
            rules={[{ required: true, message: "Please enter the admin's full name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Admin Username"
            name="admin_username"
            rules={[{ required: true, message: "Please enter the admin's username" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Admin Email"
            name="admin_email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Admin Phone"
            name="admin_phone"
            rules={[{ required: true, message: "Please enter the admin's phone number" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="admin_password"
            extra="Leave blank to keep the current password."
          >
            <Input.Password placeholder="Only fill in to change it" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageRestaurants;
